/**
 * DrawingMode.tsx - Enhanced Drawing Tool
 * 
 * Supports multiple drawing modes:
 * - POINTS: Click-to-place prediction points (original)
 * - RANGE: Draw a price range/channel
 * - PATTERN: Click-to-place for patterns (head & shoulders, etc.)
 * - FREEHAND: Draw freely with mouse/touch - rockets, dicks, whatever 🎨
 * 
 * Features:
 * - Submit prediction to Grok AI
 * - Share as screenshot without submitting
 * - Draggable points
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';

// ============ Types ============

export type DrawingModeType = 'points' | 'range' | 'pattern' | 'freehand';

export interface PredictionPoint {
  price: number;
  date: Date;
  x: number;  // Screen coordinates for rendering
  y: number;
}

export interface RangeData {
  priceHigh: number;
  priceLow: number;
  startDate: Date;
  endDate: Date;
}

export interface FreehandPath {
  points: { x: number; y: number }[];
  color: string;
}

export interface DrawingResult {
  points: PredictionPoint[];
  currentPrice: number;
  mode: DrawingModeType;
  range?: RangeData;
  freehandPaths?: FreehandPath[];
  imageBase64?: string; // For vision API - captured drawing as image
}

interface DrawingModeProps {
  isActive: boolean;
  onToggle: () => void;
  onComplete: (result: DrawingResult) => void;
  onShare?: (result: DrawingResult) => void; // Share without submitting
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  chartArea: SVGGElement | null;
  currentPrice: number | null; // Must be real live price, no fallback
  theme: any;
}

// ============ SVG to Base64 Image Capture ============

/**
 * Capture the chart SVG (including freehand drawings) as a base64 PNG image.
 * This allows Grok Vision to actually "see" the drawing, not just coordinate data.
 */
export const captureChartAsImage = (chartArea: SVGGElement | null): Promise<string | null> => {
  return new Promise((resolve) => {
    if (!chartArea) {
      console.warn('[DrawingMode] No chartArea to capture');
      resolve(null);
      return;
    }
    
    try {
      // Get the parent SVG element
      const svgElement = chartArea.ownerSVGElement;
      if (!svgElement) {
        console.warn('[DrawingMode] No parent SVG element found');
        resolve(null);
        return;
      }
      
      // Clone the SVG for clean capture
      const svgClone = svgElement.cloneNode(true) as SVGSVGElement;
      
      // Add inline styles for proper rendering
      svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      
      // Get SVG dimensions
      const svgRect = svgElement.getBoundingClientRect();
      const width = svgRect.width || 800;
      const height = svgRect.height || 400;
      
      svgClone.setAttribute('width', String(width));
      svgClone.setAttribute('height', String(height));
      
      // Serialize SVG to string
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(svgClone);
      
      // Fix common SVG issues
      svgString = svgString.replace(/currentColor/g, '#ffffff');
      
      // Create data URL
      const svgDataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
      
      // Create an image to draw on canvas
      const img = new Image();
      img.onload = () => {
        // Create canvas and draw
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.warn('[DrawingMode] No canvas context');
          resolve(null);
          return;
        }
        
        // Draw black background (for chart visibility)
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, width, height);
        
        // Draw SVG image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 PNG (without the data:image/png;base64, prefix)
        const dataUrl = canvas.toDataURL('image/png');
        const base64 = dataUrl.split(',')[1]; // Remove prefix
        
        console.log('[DrawingMode] Captured chart as image, base64 length:', base64?.length);
        resolve(base64);
      };
      
      img.onerror = (err) => {
        console.error('[DrawingMode] Failed to load SVG as image:', err);
        resolve(null);
      };
      
      img.src = svgDataUrl;
    } catch (err) {
      console.error('[DrawingMode] Error capturing chart:', err);
      resolve(null);
    }
  });
};

// ============ Styled Components ============

const DrawingControls = styled.div<{ $isActive: boolean }>`
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${props => props.$isActive ? '10px 12px' : '6px 10px'};
  background: ${props => props.$isActive ? 'rgba(20, 20, 20, 0.95)' : 'rgba(30, 30, 30, 0.7)'};
  border-radius: 8px;
  border: 1px solid ${props => props.$isActive ? 'rgba(247, 147, 26, 0.5)' : 'rgba(255,255,255,0.1)'};
  box-shadow: ${props => props.$isActive ? '0 4px 12px rgba(0, 0, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.2)'};
  z-index: 1000;
  transition: all 0.2s ease;
  opacity: ${props => props.$isActive ? 1 : 0.75};
  
  &:hover {
    opacity: 1;
    background: ${props => props.$isActive ? 'rgba(20, 20, 20, 0.95)' : 'rgba(30, 30, 30, 0.9)'};
    border-color: ${props => props.$isActive ? 'rgba(247, 147, 26, 0.5)' : 'rgba(247, 147, 26, 0.5)'};
  }
`;

const ModeSelector = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
`;

const ModeButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${props => props.$active ? '#f7931a' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#888'};
  
  &:hover {
    background: ${props => props.$active ? '#f7931a' : 'rgba(255,255,255,0.1)'};
    color: white;
  }
`;

const ControlRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ColorPicker = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  align-items: center;
`;

const ColorDot = styled.button<{ $color: string; $active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.$active ? '#fff' : 'transparent'};
  background: ${props => props.$color};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    transform: scale(1.1);
    border-color: rgba(255,255,255,0.5);
  }
`;

const BrushSizeSelector = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 0 8px;
  color: #888;
  font-size: 11px;
`;

const BrushSizeButton = styled.button<{ $active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${props => props.$active ? '#f7931a' : 'rgba(255,255,255,0.2)'};
  background: ${props => props.$active ? 'rgba(247,147,26,0.2)' : 'transparent'};
  color: white;
  cursor: pointer;
  font-size: 10px;
  
  &:hover {
    border-color: #f7931a;
  }
`;

const ControlButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #f7931a;
          color: white;
          &:hover { background: #e8850f; }
        `;
      case 'danger':
        return `
          background: rgba(244, 67, 54, 0.2);
          color: #f44336;
          border: 1px solid rgba(244, 67, 54, 0.3);
          &:hover { background: rgba(244, 67, 54, 0.3); }
        `;
      default:
        return `
          background: rgba(255, 255, 255, 0.1);
          color: white;
          &:hover { background: rgba(255, 255, 255, 0.2); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PointsInfo = styled.div`
  position: absolute;
  top: 60px;
  left: 20px;
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 220px;
  max-width: 280px;
  z-index: 999;
  color: white;
  font-size: 13px;
`;

const PointsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
`;

const PointItem = styled.li<{ $direction: 'up' | 'down' | 'neutral' }>`
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:last-child { border-bottom: none; }
  
  .arrow {
    font-size: 16px;
    color: ${props => props.$direction === 'up' ? '#4caf50' : props.$direction === 'down' ? '#f44336' : '#888'};
  }
  
  .price {
    font-weight: 600;
    color: ${props => props.$direction === 'up' ? '#4caf50' : props.$direction === 'down' ? '#f44336' : '#fff'};
  }
  
  .date {
    color: #888;
    margin-left: auto;
  }
`;

const HoverPreview = styled.div`
  position: absolute;
  pointer-events: none;
  background: rgba(247, 147, 26, 0.9);
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  z-index: 1001;
  transform: translate(-50%, -100%);
  margin-top: -10px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(247, 147, 26, 0.9);
  }
`;

const Instructions = styled.div`
  text-align: center;
  color: #888;
  font-size: 12px;
  margin-bottom: 4px;
`;

// ============ Constants ============

const MAX_POINTS = 10; // Increased for patterns
const POINT_RADIUS = 8;
const TOUCH_TARGET_SIZE = 44; // Minimum touch target for mobile
const FREEHAND_COLORS = ['#f7931a', '#4caf50', '#f44336', '#2196f3', '#9c27b0', '#ff9800'];
const BRUSH_SIZES = [2, 4, 6];

// ============ Main Component ============

export const DrawingMode: React.FC<DrawingModeProps> = ({
  isActive,
  onToggle,
  onComplete,
  onShare,
  xScale,
  yScale,
  chartArea,
  currentPrice,
  theme
}) => {
  const [points, setPoints] = useState<PredictionPoint[]>([]);
  const [drawingMode, setDrawingMode] = useState<DrawingModeType>('points');
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number; price: number; date: Date } | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  
  // Freehand drawing state
  const [freehandPaths, setFreehandPaths] = useState<FreehandPath[]>([]);
  const [currentPath, setCurrentPath] = useState<{ x: number; y: number }[]>([]);
  const [isDrawingFreehand, setIsDrawingFreehand] = useState(false);
  const [freehandColor, setFreehandColor] = useState(FREEHAND_COLORS[0]);
  const [brushSize, setBrushSize] = useState(BRUSH_SIZES[1]);
  const svgGroupRef = useRef<SVGGElement | null>(null);
  
  // ✅ REFS for event handlers - avoid stale closure issues
  const isDrawingFreehandRef = useRef(false);
  const currentPathRef = useRef<{ x: number; y: number }[]>([]);
  const freehandColorRef = useRef(FREEHAND_COLORS[0]);
  
  // Get max points based on mode
  const getMaxPoints = () => {
    switch (drawingMode) {
      case 'range': return 2;
      case 'pattern': return MAX_POINTS;
      default: return 5;
    }
  };
  
  // Format price for display
  const formatPrice = (price: number) => `$${price.toLocaleString()}`;
  
  // Format date for display
  const formatDate = (date: Date) => date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
  
  // Get direction relative to previous point or current price
  const getDirection = (point: PredictionPoint, index: number): 'up' | 'down' | 'neutral' => {
    const refPrice = index === 0 ? (currentPrice || point.price) : points[index - 1].price;
    if (Math.abs(point.price - refPrice) < 1000) return 'neutral';
    return point.price > refPrice ? 'up' : 'down';
  };

  // Check if click is near an existing point (for dragging)
  const findNearbyPoint = (clientX: number, clientY: number): number | null => {
    const chartRect = chartArea?.getBoundingClientRect();
    if (!chartRect) return null;
    
    const x = clientX - chartRect.left;
    const y = clientY - chartRect.top;
    
    const threshold = 20; // Pixels to detect nearby point
    
    for (let i = 0; i < points.length; i++) {
      const dx = points[i].x - x;
      const dy = points[i].y - y;
      if (Math.sqrt(dx * dx + dy * dy) < threshold) {
        return i;
      }
    }
    return null;
  };

  // Handle click/tap on chart to place point or start dragging
  const handleChartClick = useCallback((event: MouseEvent | TouchEvent) => {
    if (!isActive || !xScale || !yScale) return;
    
    // Freehand mode uses mousedown/touchstart differently
    if (drawingMode === 'freehand') return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Get coordinates
    let clientX: number, clientY: number;
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    // Check if clicking near existing point to start drag
    const nearbyIndex = findNearbyPoint(clientX, clientY);
    if (nearbyIndex !== null) {
      setDraggingIndex(nearbyIndex);
      return;
    }
    
    // Otherwise, add new point if under limit
    if (points.length >= getMaxPoints()) return;
    
    // Convert to chart coordinates
    const chartRect = chartArea?.getBoundingClientRect();
    if (!chartRect) return;
    
    const x = clientX - chartRect.left;
    const y = clientY - chartRect.top;
    
    // Convert to data values
    const price = Math.round(yScale.invert(y));
    const date = xScale.invert(x);
    
    // Validate: price must be positive (allow past dates for pattern analysis)
    if (price <= 0) {
      console.log('Invalid point: price must be positive');
      return;
    }
    
    const newPoint: PredictionPoint = { price, date, x, y };
    setPoints(prev => [...prev, newPoint]);
    
    const isPast = date < new Date();
    const modeLabel = drawingMode === 'range' ? '📊 Range' : drawingMode === 'pattern' ? '🎨 Pattern' : '📍 Point';
    console.log(`${modeLabel} ${points.length + 1}: ${formatPrice(price)} on ${formatDate(date)} ${isPast ? '(historical)' : '(future)'}`);
  }, [isActive, xScale, yScale, chartArea, points, drawingMode]);

  // ============ FREEHAND DRAWING HANDLERS ============
  
  // ✅ Keep refs in sync with state
  useEffect(() => {
    isDrawingFreehandRef.current = isDrawingFreehand;
  }, [isDrawingFreehand]);
  
  useEffect(() => {
    currentPathRef.current = currentPath;
  }, [currentPath]);
  
  useEffect(() => {
    freehandColorRef.current = freehandColor;
  }, [freehandColor]);
  
  // Handler for custom event from PriceChart overlay - START drawing
  const handleFreehandStartCustom = useCallback((event: Event) => {
    if (!isActive || drawingMode !== 'freehand') return;
    
    const customEvent = event as CustomEvent<{ x: number; y: number; clientX: number; clientY: number }>;
    const { x, y } = customEvent.detail;
    
    console.log('✏️ Freehand START at', x, y);
    
    // Update both state and ref
    setIsDrawingFreehand(true);
    isDrawingFreehandRef.current = true;
    
    const newPath = [{ x, y }];
    setCurrentPath(newPath);
    currentPathRef.current = newPath;
  }, [isActive, drawingMode]);

  // Handler for mousemove - ADD points to current path
  const handleFreehandMoveCustom = useCallback((event: Event) => {
    // ✅ Use ref for immediate state check (no stale closure)
    if (!isDrawingFreehandRef.current || drawingMode !== 'freehand') return;
    
    const customEvent = event as CustomEvent<{ x: number; y: number; clientX: number; clientY: number }>;
    const { x, y } = customEvent.detail;
    
    setCurrentPath(prev => {
      const newPath = [...prev, { x, y }];
      currentPathRef.current = newPath;
      return newPath;
    });
  }, [drawingMode]);

  // Handler for mouseup - END drawing and save path
  const handleFreehandEndCustom = useCallback(() => {
    // ✅ Use refs for immediate state check
    if (!isDrawingFreehandRef.current) {
      console.log('✏️ Freehand end ignored - not drawing');
      return;
    }
    
    const pathToSave = currentPathRef.current;
    const colorToUse = freehandColorRef.current;
    
    console.log('✏️ Freehand END, saving path with', pathToSave.length, 'points');
    
    // Save the path if it has enough points
    if (pathToSave.length >= 2) {
      setFreehandPaths(prev => [...prev, { 
        points: [...pathToSave], // Clone to avoid reference issues
        color: colorToUse 
      }]);
    }
    
    // Reset state and refs
    setIsDrawingFreehand(false);
    isDrawingFreehandRef.current = false;
    setCurrentPath([]);
    currentPathRef.current = [];
  }, []);
  
  // Legacy handlers for direct touch events (touch devices)
  const handleFreehandStart = useCallback((event: MouseEvent | TouchEvent) => {
    if (!isActive || drawingMode !== 'freehand') return;
    
    event.preventDefault();
    
    let clientX: number, clientY: number;
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    const chartRect = chartArea?.getBoundingClientRect();
    if (!chartRect) return;
    
    const x = clientX - chartRect.left;
    const y = clientY - chartRect.top;
    
    console.log('✏️ Freehand touch START at', x, y);
    
    // Update both state and ref
    setIsDrawingFreehand(true);
    isDrawingFreehandRef.current = true;
    
    const newPath = [{ x, y }];
    setCurrentPath(newPath);
    currentPathRef.current = newPath;
  }, [isActive, drawingMode, chartArea]);

  const handleFreehandMove = useCallback((event: MouseEvent | TouchEvent) => {
    // ✅ Use ref for immediate state check
    if (!isDrawingFreehandRef.current || drawingMode !== 'freehand') return;
    
    event.preventDefault();
    
    let clientX: number, clientY: number;
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    const chartRect = chartArea?.getBoundingClientRect();
    if (!chartRect) return;
    
    const x = clientX - chartRect.left;
    const y = clientY - chartRect.top;
    
    setCurrentPath(prev => {
      const newPath = [...prev, { x, y }];
      currentPathRef.current = newPath;
      return newPath;
    });
  }, [drawingMode, chartArea]);

  const handleFreehandEnd = useCallback(() => {
    // ✅ Use refs for immediate state check
    if (!isDrawingFreehandRef.current) return;
    
    const pathToSave = currentPathRef.current;
    const colorToUse = freehandColorRef.current;
    
    console.log('✏️ Freehand touch END, path length:', pathToSave.length);
    
    if (pathToSave.length >= 2) {
      setFreehandPaths(prev => [...prev, { 
        points: [...pathToSave],
        color: colorToUse 
      }]);
    }
    
    // Reset state and refs
    setIsDrawingFreehand(false);
    isDrawingFreehandRef.current = false;
    setCurrentPath([]);
    currentPathRef.current = [];
  }, []);

  // Handle dragging a point
  const handleDrag = useCallback((event: MouseEvent | TouchEvent) => {
    if (draggingIndex === null || !xScale || !yScale) return;
    
    let clientX: number, clientY: number;
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    const chartRect = chartArea?.getBoundingClientRect();
    if (!chartRect) return;
    
    const x = clientX - chartRect.left;
    const y = clientY - chartRect.top;
    
    const price = Math.round(yScale.invert(y));
    const date = xScale.invert(x);
    
    if (price <= 0) return;
    
    setPoints(prev => {
      const updated = [...prev];
      updated[draggingIndex] = { price, date, x, y };
      return updated;
    });
  }, [draggingIndex, xScale, yScale, chartArea]);

  // Stop dragging
  const handleDragEnd = useCallback(() => {
    setDraggingIndex(null);
  }, []);

  // Handle mouse move for hover preview (desktop only)
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isActive || !xScale || !yScale || points.length >= getMaxPoints()) {
      setHoverPosition(null);
      return;
    }
    
    const chartRect = chartArea?.getBoundingClientRect();
    if (!chartRect) return;
    
    const x = event.clientX - chartRect.left;
    const y = event.clientY - chartRect.top;
    
    // Check if within chart bounds
    if (x < 0 || y < 0 || x > chartRect.width || y > chartRect.height) {
      setHoverPosition(null);
      return;
    }
    
    const price = Math.round(yScale.invert(y));
    const date = xScale.invert(x);
    
    setHoverPosition({ x: event.clientX, y: event.clientY, price, date });
  }, [isActive, xScale, yScale, chartArea, points.length]);

  // Handle keyboard shortcuts (desktop)
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isActive) return;
    
    switch (event.key) {
      case 'Escape':
        handleClear();
        onToggle(); // Exit drawing mode
        break;
      case 'Enter':
        if (points.length > 0) {
          handleDone();
        }
        break;
      case 'z':
        if (event.ctrlKey || event.metaKey) {
          handleUndo();
        }
        break;
    }
  }, [isActive, points.length]);

  // Handle custom drawingModeClick event from PriceChart's interaction overlay
  const handleDrawingModeClick = useCallback((event: Event) => {
    // ✅ Skip in freehand mode - we don't place points, only draw paths
    if (drawingMode === 'freehand') return;
    
    if (!isActive || !xScale || !yScale || points.length >= MAX_POINTS) {
      return;
    }
    
    const customEvent = event as CustomEvent<{ x: number, y: number, time: Date, price: number }>;
    const { time, price: rawPrice } = customEvent.detail;
    const { x, y } = customEvent.detail;
    
    const price = Math.round(rawPrice);
    
    // Validate: price must be positive (allow past dates for pattern analysis)
    if (price <= 0) {
      console.log('Invalid point: price must be positive');
      return;
    }
    
    const newPoint: PredictionPoint = { price, date: time, x, y };
    setPoints(prev => [...prev, newPoint]);
    
    const isPast = time < new Date();
    console.log(`📍 Placed point ${points.length + 1}: ${formatPrice(price)} on ${formatDate(time)} ${isPast ? '(historical)' : '(future)'}`);
  }, [isActive, xScale, yScale, points.length, drawingMode]);

  // Attach event listeners when active
  useEffect(() => {
    if (!chartArea || !isActive) return;
    
    const element = chartArea;
    
    // Add listeners - use drawingModeClick custom event for click handling
    element.addEventListener('drawingModeClick', handleDrawingModeClick as EventListener);
    element.addEventListener('click', handleChartClick as EventListener);
    element.addEventListener('touchstart', handleChartClick as EventListener, { passive: false });
    element.addEventListener('mousemove', handleMouseMove as EventListener);
    document.addEventListener('keydown', handleKeyDown);
    
    // Drag listeners (on document to continue dragging outside chart)
    document.addEventListener('mousemove', handleDrag as EventListener);
    document.addEventListener('touchmove', handleDrag as EventListener);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchend', handleDragEnd);
    
    // ✅ FREEHAND: Always register freehand listeners when in freehand mode
    // Using refs ensures these handlers work correctly regardless of re-registration
    if (drawingMode === 'freehand') {
      element.addEventListener('drawingModeMouseDown', handleFreehandStartCustom as EventListener);
      element.addEventListener('drawingModeMouseMove', handleFreehandMoveCustom as EventListener);
      element.addEventListener('drawingModeMouseUp', handleFreehandEndCustom as EventListener);
      // ✅ CRITICAL: Listen on document for mouseup (user may release outside chart)
      document.addEventListener('mouseup', handleFreehandEndCustom as EventListener);
      // Touch events for mobile
      element.addEventListener('touchstart', handleFreehandStart as EventListener, { passive: false });
      document.addEventListener('touchmove', handleFreehandMove as EventListener, { passive: false });
      document.addEventListener('touchend', handleFreehandEnd);
    }
    
    // Set cursor
    element.style.cursor = 'crosshair';
    
    return () => {
      element.removeEventListener('drawingModeClick', handleDrawingModeClick as EventListener);
      element.removeEventListener('click', handleChartClick as EventListener);
      element.removeEventListener('touchstart', handleChartClick as EventListener);
      element.removeEventListener('mousemove', handleMouseMove as EventListener);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousemove', handleDrag as EventListener);
      document.removeEventListener('touchmove', handleDrag as EventListener);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchend', handleDragEnd);
      
      // Freehand cleanup (always remove to be safe)
      element.removeEventListener('drawingModeMouseDown', handleFreehandStartCustom as EventListener);
      element.removeEventListener('drawingModeMouseMove', handleFreehandMoveCustom as EventListener);
      element.removeEventListener('drawingModeMouseUp', handleFreehandEndCustom as EventListener);
      document.removeEventListener('mouseup', handleFreehandEndCustom as EventListener);
      element.removeEventListener('touchstart', handleFreehandStart as EventListener);
      document.removeEventListener('touchmove', handleFreehandMove as EventListener);
      document.removeEventListener('touchend', handleFreehandEnd);
      
      element.style.cursor = '';
    };
  // ✅ Note: Handlers use refs internally, so we don't need isDrawingFreehand in deps
  }, [chartArea, isActive, handleChartClick, handleDrawingModeClick, handleMouseMove, handleKeyDown, handleDrag, handleDragEnd, draggingIndex, drawingMode, handleFreehandStart, handleFreehandMove, handleFreehandEnd, handleFreehandStartCustom, handleFreehandMoveCustom, handleFreehandEndCustom]);

  // Render preview points and lines on chart
  useEffect(() => {
    if (!chartArea || !isActive) {
      // Clean up when not active
      d3.select(chartArea).selectAll('.drawing-preview').remove();
      return;
    }
    
    const svg = d3.select(chartArea);
    
    // Remove old preview
    svg.selectAll('.drawing-preview').remove();
    
    // Check if there's anything to render
    const hasPoints = points.length > 0;
    const hasFreehand = freehandPaths.length > 0 || currentPath.length > 0;
    if (!hasPoints && !hasFreehand) return;
    
    // Create preview group
    const previewGroup = svg.append('g').attr('class', 'drawing-preview');
    
    // RANGE MODE: Draw a shaded price band
    if (drawingMode === 'range' && points.length >= 1) {
      const prices = points.map(p => p.price);
      const dates = points.map(p => p.date);
      
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const startDate = dates.length > 1 ? new Date(Math.min(...dates.map(d => d.getTime()))) : dates[0];
      const endDate = dates.length > 1 ? new Date(Math.max(...dates.map(d => d.getTime()))) : new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000);
      
      const x1 = xScale(startDate);
      const x2 = xScale(endDate);
      const y1 = yScale(maxPrice);
      const y2 = yScale(minPrice);
      
      // Shaded area
      previewGroup.append('rect')
        .attr('x', Math.min(x1, x2))
        .attr('y', y1)
        .attr('width', Math.abs(x2 - x1))
        .attr('height', y2 - y1)
        .attr('fill', 'rgba(247, 147, 26, 0.15)')
        .attr('stroke', '#f7931a')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5');
      
      // Top line
      previewGroup.append('line')
        .attr('x1', x1).attr('y1', y1)
        .attr('x2', x2).attr('y2', y1)
        .attr('stroke', '#4caf50')
        .attr('stroke-width', 2);
      
      // Bottom line
      previewGroup.append('line')
        .attr('x1', x1).attr('y1', y2)
        .attr('x2', x2).attr('y2', y2)
        .attr('stroke', '#f44336')
        .attr('stroke-width', 2);
      
      // Price labels
      previewGroup.append('text')
        .attr('x', x2 + 5)
        .attr('y', y1 + 4)
        .attr('fill', '#4caf50')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .text(formatPrice(maxPrice));
      
      previewGroup.append('text')
        .attr('x', x2 + 5)
        .attr('y', y2 + 4)
        .attr('fill', '#f44336')
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .text(formatPrice(minPrice));
    }
    
    // POINTS & PATTERN MODES: Draw lines connecting points
    if ((drawingMode === 'points' || drawingMode === 'pattern') && points.length > 1) {
      const lineGenerator = d3.line<PredictionPoint>()
        .x(d => d.x)
        .y(d => d.y);
      
      previewGroup.append('path')
        .datum(points)
        .attr('d', lineGenerator)
        .attr('fill', 'none')
        .attr('stroke', drawingMode === 'pattern' ? '#9c27b0' : '#f7931a')
        .attr('stroke-width', drawingMode === 'pattern' ? 3 : 2)
        .attr('stroke-dasharray', drawingMode === 'pattern' ? 'none' : '5,5')
        .attr('opacity', 0.8);
    }
    
    // Draw line from current price to first point
    // Note: currentPrice is guaranteed to be a number here (we return early if null)
    if (points.length >= 1 && currentPrice) {
      const currentPriceY = yScale(currentPrice);
      const currentDate = new Date();
      const currentX = xScale(currentDate);
      
      previewGroup.append('line')
        .attr('x1', currentX)
        .attr('y1', currentPriceY)
        .attr('x2', points[0].x)
        .attr('y2', points[0].y)
        .attr('stroke', '#888')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0.5);
      
      // Current price marker
      previewGroup.append('circle')
        .attr('cx', currentX)
        .attr('cy', currentPriceY)
        .attr('r', 4)
        .attr('fill', '#888')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1);
    }
    
    // Draw points (draggable)
    points.forEach((point, index) => {
      const direction = getDirection(point, index);
      const color = direction === 'up' ? '#4caf50' : direction === 'down' ? '#f44336' : '#f7931a';
      const isDragging = draggingIndex === index;
      
      // Drag hint ring (shows on hover/drag)
      previewGroup.append('circle')
        .attr('cx', point.x)
        .attr('cy', point.y)
        .attr('r', isDragging ? POINT_RADIUS + 8 : POINT_RADIUS + 4)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', isDragging ? 3 : 1)
        .attr('stroke-dasharray', isDragging ? 'none' : '4,2')
        .attr('opacity', isDragging ? 0.8 : 0.4)
        .style('pointer-events', 'none');
      
      // Point circle
      previewGroup.append('circle')
        .attr('cx', point.x)
        .attr('cy', point.y)
        .attr('r', isDragging ? POINT_RADIUS + 2 : POINT_RADIUS)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', isDragging ? 3 : 2)
        .style('cursor', 'grab')
        .attr('class', 'draggable-point');
      
      // Point number
      previewGroup.append('text')
        .attr('x', point.x)
        .attr('y', point.y + 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('fill', '#fff')
        .style('pointer-events', 'none')
        .text(index + 1);
      
      // Price label
      previewGroup.append('text')
        .attr('x', point.x + 15)
        .attr('y', point.y - 10)
        .attr('font-size', '11px')
        .attr('font-weight', '600')
        .attr('fill', color)
        .style('pointer-events', 'none')
        .text(formatPrice(point.price));
    });
    
    // FREEHAND MODE: Render all saved paths
    freehandPaths.forEach((path, pathIndex) => {
      if (path.points.length < 2) return;
      
      const lineGenerator = d3.line<{ x: number; y: number }>()
        .x(d => d.x)
        .y(d => d.y)
        .curve(d3.curveCatmullRom.alpha(0.5)); // Smooth curve
      
      previewGroup.append('path')
        .datum(path.points)
        .attr('d', lineGenerator)
        .attr('fill', 'none')
        .attr('stroke', path.color)
        .attr('stroke-width', brushSize)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('opacity', 0.9);
    });
    
    // FREEHAND MODE: Render current drawing path
    if (currentPath.length > 1) {
      const lineGenerator = d3.line<{ x: number; y: number }>()
        .x(d => d.x)
        .y(d => d.y)
        .curve(d3.curveCatmullRom.alpha(0.5));
      
      previewGroup.append('path')
        .datum(currentPath)
        .attr('d', lineGenerator)
        .attr('fill', 'none')
        .attr('stroke', freehandColor)
        .attr('stroke-width', brushSize)
        .attr('stroke-linecap', 'round')
        .attr('stroke-linejoin', 'round')
        .attr('opacity', 0.9);
    }
    
  }, [chartArea, isActive, points, xScale, yScale, currentPrice, drawingMode, draggingIndex, freehandPaths, currentPath, freehandColor, brushSize]);

  // Action handlers
  const handleUndo = () => {
    if (drawingMode === 'freehand') {
      // Undo last freehand path
      setFreehandPaths(prev => prev.slice(0, -1));
    } else {
      setPoints(prev => prev.slice(0, -1));
    }
  };
  
  const handleClear = () => {
    setPoints([]);
    setFreehandPaths([]);
    setCurrentPath([]);
  };
  
  // Build the result object
  const buildResult = (): DrawingResult => {
    const result: DrawingResult = { 
      points, 
      currentPrice: currentPrice!, 
      mode: drawingMode,
      freehandPaths: drawingMode === 'freehand' ? freehandPaths : undefined
    };
    
    // Add range data if in range mode
    if (drawingMode === 'range' && points.length >= 2) {
      const prices = points.map(p => p.price);
      const dates = points.map(p => p.date);
      result.range = {
        priceHigh: Math.max(...prices),
        priceLow: Math.min(...prices),
        startDate: new Date(Math.min(...dates.map(d => d.getTime()))),
        endDate: new Date(Math.max(...dates.map(d => d.getTime())))
      };
    }
    
    return result;
  };
  
  // Build result with async image capture for freehand mode
  const buildResultWithImage = async (): Promise<DrawingResult> => {
    const result = buildResult();
    
    // For freehand mode, capture the chart as an image for Grok Vision
    if (drawingMode === 'freehand' && freehandPaths.length > 0) {
      console.log('[DrawingMode] 🎨 Freehand mode - capturing image for vision API...');
      console.log('[DrawingMode]   - chartArea exists:', !!chartArea);
      console.log('[DrawingMode]   - freehandPaths:', freehandPaths.length);
      try {
        const imageBase64 = await captureChartAsImage(chartArea);
        if (imageBase64) {
          result.imageBase64 = imageBase64;
          console.log('[DrawingMode] ✅ Image captured successfully, length:', imageBase64.length);
        } else {
          console.warn('[DrawingMode] ⚠️ Image capture returned null');
        }
      } catch (err) {
        console.error('[DrawingMode] ❌ Image capture failed:', err);
      }
    } else {
      console.log('[DrawingMode] Not freehand or no paths, skipping image capture');
      console.log('[DrawingMode]   - drawingMode:', drawingMode);
      console.log('[DrawingMode]   - freehandPaths:', freehandPaths.length);
    }
    
    return result;
  };
  
  const handleDone = async () => {
    console.log('[DrawingMode] handleDone called');
    console.log('[DrawingMode]   - points:', points.length);
    console.log('[DrawingMode]   - freehandPaths:', freehandPaths.length);
    console.log('[DrawingMode]   - currentPrice:', currentPrice);
    
    if ((points.length > 0 || freehandPaths.length > 0) && currentPrice) {
      const result = await buildResultWithImage();
      console.log('[DrawingMode] Result built:', {
        mode: result.mode,
        pointsCount: result.points.length,
        freehandPathsCount: result.freehandPaths?.length,
        hasImage: !!result.imageBase64,
        imageLength: result.imageBase64?.length
      });
      onComplete(result);
    }
  };
  
  const handleShare = async () => {
    if ((points.length > 0 || freehandPaths.length > 0) && currentPrice && onShare) {
      const result = await buildResultWithImage();
      onShare(result);
    }
  };

  // ⛔ TEMPORARILY DISABLED - Drawing feature hidden until improved
  // TODO: Re-enable when freehand drawing is smoother
  const DRAWING_FEATURE_ENABLED = false;

  // Don't render if no real price data available or feature disabled
  if (!currentPrice || !DRAWING_FEATURE_ENABLED) {
    return null;
  }

  // Don't render controls if not active and no toggle button needed
  // The toggle button is always shown; controls appear when active
  
  return (
    <>
      {/* Main toggle button (always visible) */}
      {!isActive && (
        <DrawingControls $isActive={false}>
          <ControlButton $variant="primary" onClick={onToggle}>
            ✏️ Draw
          </ControlButton>
        </DrawingControls>
      )}
      
      {/* Active drawing mode UI */}
      {isActive && (
        <>
          {/* Control panel - top left */}
          <DrawingControls $isActive={true}>
            {/* Mode selector */}
            <ModeSelector>
              <ModeButton 
                $active={drawingMode === 'points'}
                onClick={() => { setDrawingMode('points'); setPoints([]); setFreehandPaths([]); }}
                title="Prediction points"
              >
                📍 Points
              </ModeButton>
              <ModeButton 
                $active={drawingMode === 'range'}
                onClick={() => { setDrawingMode('range'); setPoints([]); setFreehandPaths([]); }}
                title="Price range/channel"
              >
                📊 Range
              </ModeButton>
              <ModeButton 
                $active={drawingMode === 'pattern'}
                onClick={() => { setDrawingMode('pattern'); setPoints([]); setFreehandPaths([]); }}
                title="Draw pattern"
              >
                🎨 Pattern
              </ModeButton>
              <ModeButton 
                $active={drawingMode === 'freehand'}
                onClick={() => { setDrawingMode('freehand'); setPoints([]); setFreehandPaths([]); }}
                title="Freehand draw - rockets, moons, whatever!"
              >
                ✏️ Draw
              </ModeButton>
            </ModeSelector>
            
            {/* Freehand color & brush controls */}
            {drawingMode === 'freehand' && (
              <ColorPicker>
                <span style={{ fontSize: '10px', color: '#888', marginRight: '6px' }}>Color:</span>
                {FREEHAND_COLORS.map(color => (
                  <ColorDot 
                    key={color}
                    $color={color}
                    $active={freehandColor === color}
                    onClick={() => setFreehandColor(color)}
                  />
                ))}
                <span style={{ fontSize: '10px', color: '#888', marginLeft: '8px', marginRight: '4px' }}>Size:</span>
                {BRUSH_SIZES.map(size => (
                  <BrushSizeButton
                    key={size}
                    $active={brushSize === size}
                    onClick={() => setBrushSize(size)}
                  >
                    {size === 2 ? 'S' : size === 4 ? 'M' : 'L'}
                  </BrushSizeButton>
                ))}
              </ColorPicker>
            )}
            
            {/* Action buttons */}
            <ControlRow>
              <ControlButton 
                onClick={handleUndo} 
                disabled={drawingMode === 'freehand' ? freehandPaths.length === 0 : points.length === 0}
                title="Undo last point (Ctrl+Z)"
              >
                ↩️
              </ControlButton>
              <ControlButton 
                onClick={handleClear}
                disabled={points.length === 0 && freehandPaths.length === 0}
                title="Clear all"
                style={{ background: 'rgba(255, 152, 0, 0.2)', color: '#ff9800' }}
              >
                🔄
              </ControlButton>
              <ControlButton 
                $variant="danger" 
                onClick={() => { handleClear(); onToggle(); }}
                title="Exit drawing mode (Esc)"
              >
                ✕
              </ControlButton>
              {onShare && (
                <ControlButton 
                  onClick={handleShare}
                  disabled={points.length === 0 && freehandPaths.length === 0}
                  title="Share as image"
                  style={{ background: 'rgba(156, 39, 176, 0.3)', color: '#ce93d8' }}
                >
                  📸 Share
                </ControlButton>
              )}
              <ControlButton 
                $variant="primary" 
                onClick={handleDone}
                disabled={points.length === 0 && freehandPaths.length === 0}
                title="Submit prediction (Enter)"
              >
                ✓ Submit
              </ControlButton>
            </ControlRow>
          </DrawingControls>
          
          {/* Points info panel */}
          <PointsInfo>
            {points.length === 0 && freehandPaths.length === 0 ? (
              <>
                <Instructions>
                  {drawingMode === 'freehand'
                    ? '✏️ Click & drag to draw freely!'
                    : drawingMode === 'range' 
                    ? '👆 Click 2 points to define price range'
                    : drawingMode === 'pattern'
                    ? '👆 Click to draw pattern (up to 10 pts)'
                    : '👆 Click to place prediction points'}
                </Instructions>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                  {drawingMode === 'freehand' ? (
                    <>
                      <div>• Draw rockets 🚀</div>
                      <div>• Draw moons 🌙</div>
                      <div>• Draw... whatever! 🎨</div>
                      <div>• Pick color & brush size above</div>
                    </>
                  ) : drawingMode === 'range' ? (
                    <>
                      <div>• First click: one edge</div>
                      <div>• Second click: other edge</div>
                      <div>• Creates price band</div>
                    </>
                  ) : drawingMode === 'pattern' ? (
                    <>
                      <div>• Draw head & shoulders</div>
                      <div>• Triangles, wedges, flags</div>
                      <div>• Any pattern you see! 🎨</div>
                      <div>• 📸 Share without submitting</div>
                    </>
                  ) : (
                    <>
                      <div>• Future = Price prediction</div>
                      <div>• Past = Pattern analysis</div>
                      <div>• Max 5 points</div>
                    </>
                  )}
                </div>
                {/* Navigation hint */}
                <div style={{ fontSize: '10px', color: '#555', marginTop: '10px', padding: '6px 8px', background: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>
                  💡 <strong>Scroll</strong> to zoom • <strong>Shift+drag</strong> to pan
                </div>
              </>
            ) : (
              <>
                <Instructions>
                  {drawingMode === 'freehand'
                    ? `✏️ ${freehandPaths.length} strokes`
                    : drawingMode === 'range' 
                    ? `Range: ${points.length}/2 points`
                    : `${points.length}/${getMaxPoints()} points`}
                  {drawingMode !== 'freehand' && (
                    <span style={{ color: '#888', marginLeft: '4px', fontSize: '10px' }}>
                      (drag to adjust)
                    </span>
                  )}
                </Instructions>
                {drawingMode === 'freehand' ? (
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                    Keep drawing or hit Submit/Share! 🎨
                  </div>
                ) : (
                  <PointsList>
                    {points.map((point, index) => {
                      const direction = getDirection(point, index);
                      const isPast = point.date < new Date();
                      return (
                        <PointItem key={index} $direction={direction}>
                          <span className="arrow">
                            {isPast ? '📊' : direction === 'up' ? '📈' : direction === 'down' ? '📉' : '➡️'}
                          </span>
                          <span className="price">{formatPrice(point.price)}</span>
                          <span className="date">{formatDate(point.date)}{isPast ? ' ⏪' : ''}</span>
                        </PointItem>
                      );
                    })}
                  </PointsList>
                )}
                {drawingMode === 'range' && points.length >= 2 && (
                  <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(247,147,26,0.1)', borderRadius: '4px', fontSize: '11px' }}>
                    📊 Range: {formatPrice(Math.min(...points.map(p => p.price)))} - {formatPrice(Math.max(...points.map(p => p.price)))}
                  </div>
                )}
              </>
            )}
          </PointsInfo>
          
          {/* Hover preview (desktop only) */}
          {hoverPosition && (
            <HoverPreview style={{ left: hoverPosition.x, top: hoverPosition.y }}>
              {formatPrice(hoverPosition.price)} • {formatDate(hoverPosition.date)}
            </HoverPreview>
          )}
        </>
      )}
    </>
  );
};

export default DrawingMode;
